/**
 * Choosing which browser position fix to believe, for screens where a few hundred feet decide
 * whether somebody may start an adventure.
 *
 * Browsers hand back whatever they have: a Wi-Fi or IP guess good to 300 m, a cached fix from a
 * minute ago, then (eventually) real GPS. Taken in arrival order, a person standing on the start
 * is told they are 500 feet away whenever a rough fix lands after a good one. These helpers keep
 * the best recent fix, decide when it has improved enough to ask the server again, and fetch a
 * fresh one on demand with a hard time limit.
 */
import { distanceMeters, type Coords } from './geo'

export interface Fix extends Coords {
  /** Radius of 68% confidence, metres, as the browser reports it. */
  accuracy: number
  /** When the browser took the fix (ms since epoch). */
  timestamp: number
}

/** Worse than this, a fix is a guess rather than a position (Wi-Fi / cell / IP). */
export const ROUGH_ACCURACY_M = 50

/** A browser-cached fix older than this is not accepted for a fresh reading. */
export const FIX_MAX_AGE_MS = 30_000

/** A kept fix no newer than this stops outranking a rougher but newer one. */
export const STALE_FIX_MS = 120_000

/** Closer than this, with no better accuracy, a new fix only confirms the one held. */
const JITTER_M = 5

/** How far the viewer must move before the detail is asked for again. */
export const REFETCH_MOVE_M = 25

export function fixFromPosition(position: GeolocationPosition): Fix {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    // Some browsers omit it; treat a missing figure as rough rather than perfect.
    accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : 1000,
    timestamp: Number.isFinite(position.timestamp) ? position.timestamp : Date.now(),
  }
}

export const isRough = (fix: Fix | null | undefined): boolean =>
  !fix || fix.accuracy > ROUGH_ACCURACY_M

/**
 * What to do with a newly arrived fix, given the one held and when it was last confirmed.
 *
 * - `replace`: the new fix is better, or the held one has gone stale.
 * - `confirm`: the same place at no better accuracy; keep the held fix but count it as fresh.
 * - `keep`: a rough fix while a better recent one exists; ignore it.
 */
export function chooseFix(
  held: Fix | null,
  heldConfirmedAt: number,
  next: Fix,
  now: number,
): 'replace' | 'confirm' | 'keep' {
  if (!held) return 'replace'

  const moved = distanceMeters(held, next)
  const better = next.accuracy < held.accuracy - JITTER_M

  if (moved < JITTER_M && !better) return 'confirm'
  if (better) return 'replace'

  // A good fix is news even when slightly less precise than the held one: the person moved.
  if (next.accuracy <= ROUGH_ACCURACY_M) return 'replace'

  // A rough fix only wins once the better one has gone stale.
  const stale = now - Math.max(held.timestamp, heldConfirmedAt) > STALE_FIX_MS
  return stale ? 'replace' : 'keep'
}

/**
 * Whether the detail (and its join verdict) should be asked for again from `next`, having last
 * been asked from `anchor`: on the first fix, on real movement, and when the fix sharpens enough
 * that the verdict might change.
 */
export function shouldRefetchFrom(anchor: Fix | null, next: Fix): boolean {
  if (!anchor) return true
  if (distanceMeters(anchor, next) >= REFETCH_MOVE_M) return true
  if (anchor.accuracy > ROUGH_ACCURACY_M && next.accuracy <= ROUGH_ACCURACY_M) return true
  return next.accuracy <= anchor.accuracy / 2 && anchor.accuracy - next.accuracy >= 20
}

// ---------------------------------------------------------------------------------------------
// The best recent fix, shared by every screen that reads one, so a join started from a dialog
// that never watched the position can still fall back to what the page beneath it knew.
// ---------------------------------------------------------------------------------------------

let lastGoodFix: Fix | null = null
let lastGoodFixAt = 0

export function rememberFix(fix: Fix, now = Date.now()): void {
  const verdict = chooseFix(lastGoodFix, lastGoodFixAt, fix, now)
  if (verdict === 'replace') lastGoodFix = fix
  if (verdict !== 'keep') lastGoodFixAt = now
}

/** The best fix seen in the last STALE_FIX_MS, or null. */
export function recentFix(now = Date.now()): Fix | null {
  if (!lastGoodFix) return null
  return now - Math.max(lastGoodFix.timestamp, lastGoodFixAt) <= STALE_FIX_MS ? lastGoodFix : null
}

/** Test seam. */
export function forgetFixes(): void {
  lastGoodFix = null
  lastGoodFixAt = 0
}

/**
 * A fresh high-accuracy fix, or the best recent one when the browser cannot produce one in time.
 *
 * Bounded by its own timer as well as the browser's `timeout`, because the browser's clock does
 * not run while a permission prompt is open, and a join must never hang on that.
 */
export function getFreshFix(timeoutMs = 5000): Promise<Fix | null> {
  const fallback = () => recentFix()
  const geo = typeof navigator !== 'undefined' ? navigator.geolocation : undefined
  if (!geo) return Promise.resolve(fallback())

  return new Promise((resolve) => {
    let settled = false
    const finish = (fix: Fix | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(fix)
    }
    const timer = setTimeout(() => finish(fallback()), timeoutMs + 500)
    try {
      geo.getCurrentPosition(
        (position) => {
          const fix = fixFromPosition(position)
          rememberFix(fix)
          // A rough fresh reading does not beat a precise one from moments ago.
          finish(recentFix() ?? fix)
        },
        () => finish(fallback()),
        { enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs },
      )
    } catch {
      finish(fallback())
    }
  })
}
